import React, { useState, useEffect } from 'react';
import { Form, Button, Container, Card, Spinner, Row, Col, Tab, Tabs, Badge, Collapse, ListGroup, Modal } from 'react-bootstrap';
import { toast, ToastContainer } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { FaBook, FaVideo, FaClipboardCheck, FaPlus, FaCog, FaList, FaFilePdf, FaToggleOn, FaToggleOff, FaChevronDown, FaChevronUp, FaYoutube, FaCheckCircle, FaTimesCircle, FaEye, FaEdit, FaQuestionCircle } from 'react-icons/fa';
import 'react-toastify/dist/ReactToastify.css';
import '../styles/Capacitaciones.css';
import CapacitacionCard from '../components/CapacitacionCard';

const API_URL = (process.env.REACT_APP_API_URL || "/").replace(/\/$/, '');

// Mapeo de roles
const ROLES_MAP = {
   0: 'Todos',
   1: 'Administración',
   2: 'Inspección',
   3: 'Taller',
   4: 'Conducción'
};

const CargarCapacitaciones = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('modulo');
  const [categorias, setCategorias] = useState([]);
  const [modulosCount, setModulosCount] = useState(0);
  const [loadingModulos, setLoadingModulos] = useState(false);
  const [loadingCategoria, setLoadingCategoria] = useState(false);
  
  // Nuevos estados para ver módulos y evaluaciones
  const [modulosPorCategoria, setModulosPorCategoria] = useState({});
  const [evaluacionesPorModulo, setEvaluacionesPorModulo] = useState({});
  const [categoriaExpandida, setCategoriaExpandida] = useState(null);
  const [moduloExpandido, setModuloExpandido] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewContent, setPreviewContent] = useState({ tipo: '', url: '' });
  
  const [nuevaCategoria, setNuevaCategoria] = useState({
    nombre: '',
    descripcion: '',
    activo: true,
    rol_destinado: '0'
  });

  const usuario = JSON.parse(localStorage.getItem('user'));

  const [form, setForm] = useState({
    categoria_id: '',
    titulo: '',
    orden: '',
    video_url: '',
    tipo_contenido: 'youtube',
    requiere_aprobacion: true
  });

  const [archivoFile, setArchivoFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const cargarCategorias = () => {
    const token = localStorage.getItem('token');
    
    fetch(`${API_URL}/api/capacitaciones/categorias`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) setCategorias(data.data);
      })
      .catch(() => toast.error('Error al cargar categorías'));
  };

  // Cargar módulos de una categoría
  const cargarModulos = async (categoriaId) => {
    if (modulosPorCategoria[categoriaId]) return; // Ya cargados
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/capacitaciones/modulos/${categoriaId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setModulosPorCategoria(prev => ({
          ...prev,
          [categoriaId]: data.data
        }));
        
        // Cargar evaluaciones de cada módulo
        for (const modulo of data.data) {
          cargarEvaluacion(modulo.id);
        }
      }
    } catch (error) {
      console.error('Error al cargar módulos:', error);
      toast.error('Error al cargar módulos');
    }
  };

  // Cargar evaluación de un módulo
  const cargarEvaluacion = async (moduloId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/capacitaciones/evaluacion/${moduloId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setEvaluacionesPorModulo(prev => ({
          ...prev,
          [moduloId]: data.data
        }));
      }
    } catch (error) {
      console.error('Error al cargar evaluación:', error);
    }
  };

  // Toggle expandir categoría
  const handleExpandirCategoria = (categoriaId) => {
    if (categoriaExpandida === categoriaId) {
      setCategoriaExpandida(null);
    } else {
      setCategoriaExpandida(categoriaId);
      cargarModulos(categoriaId);
    }
  };

  // Toggle expandir módulo
  const handleExpandirModulo = (moduloId) => {
    setModuloExpandido(moduloExpandido === moduloId ? null : moduloId);
  };

  // Preview de contenido
  const handlePreviewContent = (modulo) => {
    setPreviewContent({
      tipo: modulo.tipo_contenido,
      url: modulo.video_url,
      titulo: modulo.titulo
    });
    setShowPreviewModal(true);
  };

  const toggleActivoCategoria = async (id, estadoActual) => {
    const token = localStorage.getItem('token');
    
    try {
      const res = await fetch(`${API_URL}/api/capacitaciones/categorias/${id}/toggle-activo`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success(data.mensaje);
        cargarCategorias(); // Recargar lista
      } else {
        toast.error(data.error || 'Error al cambiar estado');
      }
    } catch (error) {
      toast.error('Error al cambiar estado de la capacitación');
    }
  };

  useEffect(() => {
    cargarCategorias();
  }, []);

  const obtenerSiguienteModulo = async (categoriaId) => {
    if (!categoriaId) {
      setModulosCount(0);
      setForm(prev => ({ ...prev, orden: '' }));
      return;
    }

    setLoadingModulos(true);
    try {
      const token = localStorage.getItem('token');
      
      const res = await fetch(`${API_URL}/api/capacitaciones/modulos/${categoriaId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await res.json();
      
      if (data.success && Array.isArray(data.data)) {
        const siguienteNumero = data.data.length + 1;
        setModulosCount(data.data.length);
        setForm(prev => ({ ...prev, orden: siguienteNumero.toString() }));
      } else {
        setModulosCount(0);
        setForm(prev => ({ ...prev, orden: '1' }));
      }
    } catch {
      setForm(prev => ({ ...prev, orden: '1' }));
    } finally {
      setLoadingModulos(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'categoria_id') {
      setForm(prev => ({ ...prev, [name]: value }));
      obtenerSiguienteModulo(value);
    } else {
      setForm(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleNuevaCategoriaChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNuevaCategoria(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const crearCategoria = async () => {
    if (!nuevaCategoria.nombre.trim()) {
      toast.error('Ingresá un nombre para la capacitación');
      return;
    }

    setLoadingCategoria(true);
    try {
      const token = localStorage.getItem('token');
      
      const res = await fetch(`${API_URL}/api/capacitaciones/categorias`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json' 
        },
        body: JSON.stringify(nuevaCategoria)
      });

      const data = await res.json();
      if (!data.success) throw new Error('Error al crear capacitación');

      toast.success('✅ Capacitación creada correctamente');
      setNuevaCategoria({ nombre: '', descripcion: '', activo: true, rol_destinado: '0' });
      cargarCategorias();
      setActiveTab('gestion'); // Ir al listado después de crear
    } catch {
      toast.error('No se pudo crear la capacitación');
    } finally {
      setLoadingCategoria(false);
    }
  };

  const handleArchivoUpload = async (moduloId) => {
    const formData = new FormData();
    formData.append('archivo', archivoFile);

    const usuario = JSON.parse(localStorage.getItem('user'));
    const token = localStorage.getItem('token');

    const headers = {
      'Authorization': `Bearer ${token}`,
      'x-user-id': usuario?.CdEmpleado || usuario?.Legajo || '',
      'x-user-name': usuario?.Nombre || '',
      'x-user-role': String(usuario?.IdRol || ''),
      'x-base-id': usuario?.base_id || ''
    };

    const endpoint = `${API_URL}/api/capacitaciones/modulos/${moduloId}/archivo`;

    const res = await fetch(endpoint, {
      method: 'POST',
      headers,
      body: formData
    });

    const data = await res.json();
    if (!data.success) throw new Error(data.error || 'Error al subir archivo');

    return { url: data.video_url, tipo: data.tipo_contenido };
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.categoria_id || !form.titulo || !form.orden) {
      toast.error('Completá todos los campos obligatorios');
      return;
    }

    // Validar que tenga al menos URL o archivo
    if (!form.video_url && !archivoFile) {
      toast.error('Debés ingresar una URL de YouTube o subir un archivo');
      return;
    }

    setLoading(true);

    try {
      let finalVideoUrl = form.video_url;
      let finalTipoContenido = form.tipo_contenido;

      // Primero crear el módulo
      const payload = {
        ...form,
        video_url: form.video_url || '', // Enviar la URL si existe
        tipo_contenido: form.tipo_contenido,
        subido_por: usuario?.Legajo
      };

      // Construir headers de usuario
      const token = localStorage.getItem('token');
      
      const usuarioHeaders = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-user-id': usuario?.CdEmpleado || usuario?.Legajo || '',
        'x-user-name': usuario?.Nombre || '',
        'x-user-role': String(usuario?.IdRol || ''),
        'x-base-id': usuario?.base_id || ''
      };

      const resModulo = await fetch(`${API_URL}/api/capacitaciones/modulos`, {
        method: 'POST',
        headers: usuarioHeaders,
        body: JSON.stringify(payload)
      });
      const dataModulo = await resModulo.json();
      if (!dataModulo.success || !dataModulo.id) throw new Error('Error al registrar módulo');
      const moduloId = dataModulo.id;

      // Si hay archivo, subirlo y actualizar el módulo
      if (archivoFile) {
        const resultado = await handleArchivoUpload(moduloId);
        finalVideoUrl = resultado.url;
        finalTipoContenido = resultado.tipo;

        // Actualizar el módulo con la URL y tipo
        await fetch(`${API_URL}/api/capacitaciones/modulos/${moduloId}`, {
          method: 'PUT',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json' 
          },
          body: JSON.stringify({
            video_url: finalVideoUrl,
            tipo_contenido: finalTipoContenido
          })
        });
      }

      toast.success('✅ Módulo creado correctamente');
      setForm({ categoria_id: '', titulo: '', orden: '', video_url: '', tipo_contenido: 'youtube', requiere_aprobacion: true });
      setArchivoFile(null);
      setModulosCount(0);
    } catch {
      toast.error('No se pudo crear el módulo');
    } finally {
      setLoading(false);
    }
  };

  const categoriaSeleccionada = categorias.find(c => c.id.toString() === form.categoria_id);

  // Renderizar evaluación de un módulo
  const renderEvaluacion = (moduloId) => {
    const preguntas = evaluacionesPorModulo[moduloId] || [];
    
    if (preguntas.length === 0) {
      return (
        <div className="alert alert-info mb-0">
          <FaQuestionCircle className="me-2" />
          Este módulo no tiene evaluación configurada aún
        </div>
      );
    }

    return (
      <div className="evaluacion-container">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0">
            <FaClipboardCheck className="me-2 text-success" />
            Evaluación ({preguntas.length} preguntas)
          </h6>
        </div>
        
        <ListGroup variant="flush">
          {preguntas.map((pregunta, idx) => (
            <ListGroup.Item key={pregunta.id} className="px-0">
              <div className="mb-2">
                <Badge bg="secondary" className="me-2">#{idx + 1}</Badge>
                <strong>{pregunta.pregunta}</strong>
              </div>
              
              <div className="opciones-list ms-4">
                {['a', 'b', 'c', 'd'].map(opcion => {
                  const textoOpcion = pregunta[`opcion_${opcion}`];
                  const esCorrecta = pregunta.correcta === opcion;
                  
                  if (!textoOpcion) return null;
                  
                  return (
                    <div 
                      key={opcion} 
                      className={`opcion-item py-2 px-3 mb-2 rounded ${esCorrecta ? 'opcion-correcta' : 'opcion-normal'}`}
                    >
                      <div className="d-flex align-items-center">
                        {esCorrecta ? (
                          <FaCheckCircle className="text-success me-2" />
                        ) : (
                          <FaTimesCircle className="text-muted me-2" />
                        )}
                        <strong className="me-2 text-uppercase">{opcion})</strong>
                        <span>{textoOpcion}</span>
                        {esCorrecta && (
                          <Badge bg="success" className="ms-auto">
                            Respuesta Correcta
                          </Badge>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </ListGroup.Item>
          ))}
        </ListGroup>
      </div>
    );
  };

  return (
    <Container className="py-4">
      {/* Header */}
      <div className="capacitaciones-header mb-4">
        <h2 className="mb-1">
          <FaBook className="me-2" />
          Gestión de Capacitaciones
        </h2>
        <p className="text-muted mb-0">Administrá módulos, capacitaciones y evaluaciones</p>
      </div>

      {/* Tabs principales */}
      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="capacitaciones-tabs mb-4"
      >
        {/* TAB 1: Cargar Módulo */}
        <Tab 
          eventKey="modulo" 
          title={<><FaVideo className="me-2" />Cargar Módulo</>}
        >
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-4">
              <h5 className="card-title-custom mb-4">
                <span className="title-icon bg-primary">
                  <FaVideo />
                </span>
                Agregar nuevo módulo
              </h5>

              <Form onSubmit={handleSubmit}>
                <Row className="g-3">
                  <Col md={8}>
                    <Form.Group>
                      <Form.Label className="form-label-custom">Capacitación</Form.Label>
                      <Form.Select 
                        name="categoria_id" 
                        value={form.categoria_id} 
                        onChange={handleChange} 
                        required
                        className="form-control-lg"
                      >
                        <option value="">Seleccionar capacitación...</option>
                        {categorias.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.nombre}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="form-label-custom">
                        N° de Módulo
                        {loadingModulos && <Spinner animation="border" size="sm" className="ms-2" />}
                      </Form.Label>
                      <Form.Control 
                        type="number" 
                        name="orden" 
                        value={form.orden} 
                        onChange={handleChange} 
                        required
                        className="form-control-lg"
                        min="1"
                        disabled={loadingModulos}
                      />
                      {modulosCount > 0 && (
                        <Form.Text className="text-muted">
                          {modulosCount} módulo(s) existente(s)
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                {categoriaSeleccionada && (
                  <div className="alert alert-light border mt-3 mb-3">
                    <strong>{categoriaSeleccionada.nombre}</strong>
                    {categoriaSeleccionada.descripcion && (
                      <p className="mb-0 mt-1 small text-muted">{categoriaSeleccionada.descripcion}</p>
                    )}
                  </div>
                )}

                <Form.Group className="mt-3">
                  <Form.Label className="form-label-custom">Título del módulo</Form.Label>
                  <Form.Control 
                    type="text" 
                    name="titulo" 
                    value={form.titulo} 
                    onChange={handleChange} 
                    required
                    placeholder="Ej: Introducción a la seguridad vial"
                  />
                </Form.Group>

                {/* Selector de tipo de contenido */}
                <Form.Group className="mt-3">
                  <Form.Label className="form-label-custom">Tipo de contenido</Form.Label>
                  <div className="d-flex gap-3">
                    <Form.Check
                      type="radio"
                      id="tipo-youtube"
                      name="tipo_contenido"
                      label={<><FaVideo className="me-1" /> Video de YouTube</>}
                      value="youtube"
                      checked={form.tipo_contenido === 'youtube'}
                      onChange={handleChange}
                    />
                    <Form.Check
                      type="radio"
                      id="tipo-video"
                      name="tipo_contenido"
                      label={<><FaVideo className="me-1" /> Video (subir archivo)</>}
                      value="video"
                      checked={form.tipo_contenido === 'video'}
                      onChange={handleChange}
                    />
                    <Form.Check
                      type="radio"
                      id="tipo-pdf"
                      name="tipo_contenido"
                      label={<><FaFilePdf className="me-1" /> PDF</>}
                      value="pdf"
                      checked={form.tipo_contenido === 'pdf'}
                      onChange={handleChange}
                    />
                  </div>
                </Form.Group>

                <Row className="mt-3 g-3">
                  {form.tipo_contenido === 'youtube' && (
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label className="form-label-custom">URL del video de YouTube</Form.Label>
                        <Form.Control 
                          type="text" 
                          name="video_url" 
                          value={form.video_url} 
                          onChange={handleChange} 
                          placeholder="https://youtube.com/watch?v=..."
                        />
                      </Form.Group>
                    </Col>
                  )}
                  {form.tipo_contenido === 'video' && (
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label className="form-label-custom">Subir video</Form.Label>
                        <Form.Control 
                          type="file" 
                          accept="video/mp4,video/webm,video/quicktime,video/x-msvideo"
                          onChange={(e) => setArchivoFile(e.target.files[0])}
                        />
                        <Form.Text className="text-muted">
                          Formatos aceptados: MP4, WebM, MOV, AVI (máx. 100MB)
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  )}
                  {form.tipo_contenido === 'pdf' && (
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label className="form-label-custom">Subir PDF</Form.Label>
                        <Form.Control 
                          type="file" 
                          accept="application/pdf"
                          onChange={(e) => setArchivoFile(e.target.files[0])}
                        />
                        <Form.Text className="text-muted">
                          Formato PDF (máx. 100MB)
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  )}
                </Row>

                <Form.Group className="mt-4">
                  <Form.Check
                    type="switch"
                    id="requiere_aprobacion"
                    label="Requiere evaluación para aprobar"
                    name="requiere_aprobacion"
                    checked={form.requiere_aprobacion}
                    onChange={handleChange}
                  />
                </Form.Group>

                <div className="d-flex gap-2 mt-4 pt-2">
                  <Button 
                    type="submit" 
                    className="btn-custom-primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <><Spinner animation="border" size="sm" className="me-2" />Guardando...</>
                    ) : (
                      <><FaPlus className="me-2" />Crear Módulo</>
                    )}
                  </Button>
                  
                  {form.requiere_aprobacion && form.categoria_id && (
                    <Button 
                      className="btn-custom-outline"
                      onClick={() => navigate('/admin/capacitaciones/evaluacion')}
                    >
                      <FaClipboardCheck className="me-2" />
                      Cargar Evaluación
                    </Button>
                  )}
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Tab>

        {/* TAB 2: Nueva Capacitación */}
        <Tab 
          eventKey="capacitacion" 
          title={<><FaPlus className="me-2" />Nueva Capacitación</>}
        >
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-4">
              <h5 className="card-title-custom mb-4">
                <span className="title-icon bg-success">
                  <FaBook />
                </span>
                Crear nueva capacitación
              </h5>

              <Row className="g-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="form-label-custom">Nombre</Form.Label>
                    <Form.Control
                      type="text"
                      name="nombre"
                      value={nuevaCategoria.nombre}
                      onChange={handleNuevaCategoriaChange}
                      placeholder="Ej: Seguridad e Higiene"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="form-label-custom">Destinado a</Form.Label>
                    <Form.Select
                      name="rol_destinado"
                      value={nuevaCategoria.rol_destinado}
                      onChange={handleNuevaCategoriaChange}
                    >
                      <option value="0">Todos los roles</option>
                      <option value="1">Administración</option>
                      <option value="2">Inspección</option>
                      <option value="3">Taller</option>
                      <option value="4">Conducción</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mt-3">
                <Form.Label className="form-label-custom">Descripción</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="descripcion"
                  value={nuevaCategoria.descripcion}
                  onChange={handleNuevaCategoriaChange}
                  placeholder="Descripción breve de la capacitación..."
                />
              </Form.Group>

              <Form.Group className="mt-4">
                <Form.Check
                  type="switch"
                  id="activo"
                  label="Capacitación activa (visible para usuarios)"
                  name="activo"
                  checked={nuevaCategoria.activo}
                  onChange={handleNuevaCategoriaChange}
                />
              </Form.Group>

              <Button 
                className="btn-custom-success mt-4"
                onClick={crearCategoria}
                disabled={loadingCategoria}
              >
                {loadingCategoria ? (
                  <><Spinner animation="border" size="sm" className="me-2" />Creando...</>
                ) : (
                  <><FaPlus className="me-2" />Crear Capacitación</>
                )}
              </Button>
            </Card.Body>
          </Card>
        </Tab>

        {/* TAB 3: Gestionar Capacitaciones */}
        <Tab 
          eventKey="gestion" 
          title={<><FaList className="me-2" />Ver Capacitaciones</>}
        >
          {/* Vista mejorada con módulos y evaluaciones expandibles */}
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-4">
              <h5 className="card-title-custom mb-4">
                <span className="title-icon bg-info">
                  <FaCog />
                </span>
                Capacitaciones existentes
              </h5>
              {categorias.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <FaBook size={48} className="mb-3 opacity-50" />
                  <p>No hay capacitaciones creadas aún</p>
                  <Button 
                    className="btn-custom-primary"
                    onClick={() => setActiveTab('capacitacion')}
                  >
                    <FaPlus className="me-2" />
                    Crear primera capacitación
                  </Button>
                </div>
              ) : (
                <div className="capacitaciones-list">
                  {categorias.map(cat => (
                    <Card key={cat.id} className="mb-3 capacitacion-card">
                      {/* Header de la capacitación */}
                      <Card.Header 
                        className="capacitacion-header d-flex justify-content-between align-items-center"
                        style={{ cursor: 'pointer', backgroundColor: cat.activo ? '#f8f9fa' : '#e9ecef' }}
                        onClick={() => handleExpandirCategoria(cat.id)}
                      >
                        <div className="d-flex align-items-center flex-grow-1">
                          <div className="me-3">
                            {categoriaExpandida === cat.id ? <FaChevronUp /> : <FaChevronDown />}
                          </div>
                          <div>
                            <h6 className="mb-1 fw-bold">
                              {cat.nombre}
                              <Badge 
                                bg={cat.activo ? 'success' : 'secondary'} 
                                className="ms-2"
                              >
                                {cat.activo ? 'Activa' : 'Inactiva'}
                              </Badge>
                              <Badge bg="info" className="ms-2">
                                {ROLES_MAP[cat.rol_destinado]}
                              </Badge>
                            </h6>
                            <small className="text-muted">{cat.descripcion || 'Sin descripción'}</small>
                          </div>
                        </div>
                        
                        <div className="d-flex gap-2" onClick={(e) => e.stopPropagation()}>
                          <Button
                            size="sm"
                            variant={cat.activo ? "outline-danger" : "outline-success"}
                            onClick={() => toggleActivoCategoria(cat.id, cat.activo)}
                            title={cat.activo ? "Desactivar capacitación" : "Activar capacitación"}
                          >
                            {cat.activo ? <FaToggleOff /> : <FaToggleOn />}
                          </Button>
                        </div>
                      </Card.Header>
                      
                      {/* Contenido expandible - Módulos */}
                      <Collapse in={categoriaExpandida === cat.id}>
                        <Card.Body className="pt-3 pb-3" style={{ backgroundColor: '#fafbfc' }}>
                          {!modulosPorCategoria[cat.id] ? (
                            <div className="text-center py-3">
                              <Spinner animation="border" size="sm" />
                              <small className="d-block mt-2 text-muted">Cargando módulos...</small>
                            </div>
                          ) : modulosPorCategoria[cat.id].length === 0 ? (
                            <div className="text-center py-3 text-muted">
                              <FaVideo className="mb-2" size={32} />
                              <p className="mb-0">Esta capacitación no tiene módulos aún</p>
                            </div>
                          ) : (
                            <div className="modulos-list">
                              <h6 className="mb-3 text-muted">
                                <FaVideo className="me-2" />
                                Módulos ({modulosPorCategoria[cat.id].length})
                              </h6>
                              
                              {modulosPorCategoria[cat.id].map((modulo, idx) => (
                                <Card key={modulo.id} className="mb-2 modulo-card">
                                  <Card.Body className="p-3">
                                    <div className="d-flex justify-content-between align-items-start">
                                      <div className="flex-grow-1">
                                        <div className="d-flex align-items-center mb-2">
                                          {modulo.tipo_contenido === 'youtube' && <FaYoutube className="text-danger me-2" />}
                                          {modulo.tipo_contenido === 'pdf' && <FaFilePdf className="text-danger me-2" />}
                                          {modulo.tipo_contenido === 'video' && <FaVideo className="text-primary me-2" />}
                                          
                                          <strong className="me-2">Módulo {modulo.orden}:</strong>
                                          <span>{modulo.titulo}</span>
                                          
                                          {modulo.requiere_aprobacion === 1 && (
                                            <Badge bg="warning" className="ms-2" text="dark">
                                              Requiere Evaluación
                                            </Badge>
                                          )}
                                        </div>
                                        
                                        {modulo.video_url && (
                                          <div className="mb-2">
                                            <small className="text-muted">
                                              <strong>Contenido:</strong> {modulo.video_url.length > 60 ? modulo.video_url.substring(0, 60) + '...' : modulo.video_url}
                                            </small>
                                          </div>
                                        )}
                                        
                                        {/* Botón para expandir/ver evaluación */}
                                        {modulo.requiere_aprobacion === 1 && (
                                          <Button 
                                            size="sm" 
                                            variant="outline-primary"
                                            onClick={() => handleExpandirModulo(modulo.id)}
                                            className="mt-2"
                                          >
                                            {moduloExpandido === modulo.id ? (
                                              <><FaChevronUp className="me-1" /> Ocultar Evaluación</>
                                            ) : (
                                              <><FaChevronDown className="me-1" /> Ver Evaluación</>
                                            )}
                                          </Button>
                                        )}
                                        
                                        {/* Evaluación expandible */}
                                        <Collapse in={moduloExpandido === modulo.id}>
                                          <div className="mt-3">
                                            {renderEvaluacion(modulo.id)}
                                          </div>
                                        </Collapse>
                                      </div>
                                      
                                      <div className="d-flex gap-2 ms-3">
                                        {modulo.video_url && (
                                          <Button
                                            size="sm"
                                            variant="outline-info"
                                            onClick={() => handlePreviewContent(modulo)}
                                            title="Ver contenido"
                                          >
                                            <FaEye />
                                          </Button>
                                        )}
                                        <Button
                                          size="sm"
                                          variant="outline-secondary"
                                          title="Editar módulo"
                                        >
                                          <FaEdit />
                                        </Button>
                                      </div>
                                    </div>
                                  </Card.Body>
                                </Card>
                              ))}
                            </div>
                          )}
                        </Card.Body>
                      </Collapse>
                    </Card>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>

        {/* TAB 4: Evaluaciones */}
        <Tab 
          eventKey="evaluacion" 
          title={<><FaClipboardCheck className="me-2" />Evaluaciones</>}
        >
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-5 text-center">
              <div className="evaluaciones-empty">
                <FaClipboardCheck size={56} className="text-warning mb-3" />
                <h5>Gestión de Evaluaciones</h5>
                <p className="text-muted mb-4">
                  Creá y administrá las evaluaciones para cada módulo de capacitación
                </p>
                <Button 
                  className="btn-custom-warning"
                  onClick={() => navigate('/administrador/cargar-evaluacion')}
                >
                  <FaClipboardCheck className="me-2" />
                  Ir a Cargar Evaluaciones
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      {/* Modal para Preview de Contenido */}
      <Modal 
        show={showPreviewModal} 
        onHide={() => setShowPreviewModal(false)}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {previewContent.tipo === 'youtube' && <FaYoutube className="me-2 text-danger" />}
            {previewContent.tipo === 'pdf' && <FaFilePdf className="me-2 text-danger" />}
            {previewContent.tipo === 'video' && <FaVideo className="me-2 text-primary" />}
            {previewContent.titulo || 'Vista Previa'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {previewContent.tipo === 'youtube' && (
            <div className="ratio ratio-16x9">
              <iframe
                src={previewContent.url}
                title="Video Preview"
                allowFullScreen
                style={{ border: 'none' }}
              />
            </div>
          )}
          {previewContent.tipo === 'video' && (
            <div className="ratio ratio-16x9">
              <video controls style={{ width: '100%', height: '100%' }}>
                <source src={previewContent.url} type="video/mp4" />
                Tu navegador no soporta el elemento de video.
              </video>
            </div>
          )}
          {previewContent.tipo === 'pdf' && (
            <div style={{ height: '80vh' }}>
              <iframe
                src={previewContent.url}
                title="PDF Preview"
                style={{ width: '100%', height: '100%', border: 'none' }}
              />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPreviewModal(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer position="bottom-right" />
    </Container>
  );
}

export default CargarCapacitaciones;